import { Card } from './Card';
import { CardColor, CardType } from '@/types/card';
import { Player, Team } from '@/types/player';
import { DrawPenaltyType } from '@/types/game';
import { LIFE_POINTS_CONFIG } from '@/utils/constants';

/**
 * ゲームルールクラス
 * UNOのルール検証ロジックを提供
 */
export class GameRules {
  /**
   * カードをプレイできるか判定
   * @param card プレイするカード
   * @param topCard 捨て札の一番上のカード
   * @param declaredColor ワイルドカードで宣言された色
   * @returns プレイ可能ならtrue
   */
  static canPlayCard(
    card: Card,
    topCard: Card,
    declaredColor?: CardColor
  ): boolean {
    return card.canPlayOn(topCard, declaredColor);
  }

  /**
   * ペナルティ状況を考慮してカードをプレイできるか判定
   * Draw2/Draw4のペナルティが残っている場合、スタック可能なカードのみプレイ可能
   * @param card プレイするカード
   * @param topCard 捨て札の一番上のカード
   * @param declaredColor ワイルドカードで宣言された色
   * @param drawPenaltyType 現在のペナルティ種別（null ならペナルティなし）
   * @param allowStacking スタッキングが許可されているか
   * @returns プレイ可能ならtrue
   */
  static canPlayCardWithPenalty(
    card: Card,
    topCard: Card,
    declaredColor: CardColor | undefined,
    drawPenaltyType: DrawPenaltyType,
    allowStacking: boolean
  ): boolean {
    // 通常のプレイ可否チェック
    if (!card.canPlayOn(topCard, declaredColor)) {
      return false;
    }

    // ペナルティが無い場合は通常通り
    if (!drawPenaltyType) {
      return true;
    }

    // ペナルティ中はスタッキング許可時のみ同種カードでスタック可能
    if (!allowStacking) {
      // スタック禁止時はペナルティを回避できない（手札からプレイ不可）
      return false;
    }

    // Draw2にはDraw2のみスタック可能
    if (drawPenaltyType === 'DRAW_TWO') {
      return card.type === CardType.DRAW_TWO;
    }

    // Wild Draw4にはWild Draw4のみスタック可能
    if (drawPenaltyType === 'WILD_DRAW_FOUR') {
      return card.type === CardType.WILD_DRAW_FOUR;
    }

    return false;
  }

  /**
   * 次のプレイヤーインデックスを取得
   * @param currentIndex 現在のプレイヤーインデックス
   * @param direction ターン方向（1: 時計回り、-1: 反時計回り）
   * @param playerCount プレイヤー総数
   * @returns 次のプレイヤーインデックス
   */
  static getNextPlayerIndex(
    currentIndex: number,
    direction: 1 | -1,
    playerCount: number
  ): number {
    const nextIndex = currentIndex + direction;
    if (nextIndex >= playerCount) return 0;
    if (nextIndex < 0) return playerCount - 1;
    return nextIndex;
  }

  /**
   * 次のプレイヤーをスキップすべきか判定
   * @param card プレイされたカード
   * @returns スキップすべきならtrue
   */
  static shouldSkipNextPlayer(card: Card): boolean {
    return card.type === CardType.SKIP;
  }

  /**
   * ターン方向を反転すべきか判定
   * @param card プレイされたカード
   * @returns 反転すべきならtrue
   */
  static shouldReverseDirection(card: Card): boolean {
    return card.type === CardType.REVERSE;
  }

  /**
   * ドロー枚数を取得
   * @param card プレイされたカード
   * @returns ドロー枚数
   */
  static getDrawCount(card: Card): number {
    if (card.type === CardType.DRAW_TWO) return 2;
    if (card.type === CardType.WILD_DRAW_FOUR) return 4;
    return 0;
  }

  /**
   * ペナルティを回避できるカードを持っているか判定
   * スタッキングが有効な場合、同種のカードでペナルティを回避可能
   * @param hand プレイヤーの手札
   * @param drawPenaltyType 現在のペナルティ種別
   * @param allowStacking スタッキングが許可されているか
   * @returns 回避可能ならtrue
   */
  static canAvoidDrawPenalty(
    hand: Card[],
    drawPenaltyType: DrawPenaltyType,
    allowStacking: boolean
  ): boolean {
    // スタッキング不許可またはペナルティなしの場合は回避不可
    if (!allowStacking || !drawPenaltyType) return false;

    // Draw2ペナルティはDraw2でのみ回避可能
    if (drawPenaltyType === 'DRAW_TWO') {
      return hand.some(card => card.type === CardType.DRAW_TWO);
    }

    // Draw4ペナルティはDraw4でのみ回避可能
    if (drawPenaltyType === 'WILD_DRAW_FOUR') {
      return hand.some(card => card.type === CardType.WILD_DRAW_FOUR);
    }

    return false;
  }

  /**
   * 勝利条件をチェック
   * @param players プレイヤー配列
   * @returns 勝利したチーム（勝利チームがなければnull）
   */
  static checkWinCondition(players: Player[]): Team | null {
    // チーム1（プレイヤー1 + プレイヤー2）のどちらかが手札0枚なら勝利
    const team1Players = players.filter(p => p.team === Team.TEAM_1);
    if (team1Players.some(p => p.hand.length === 0)) {
      return Team.TEAM_1;
    }

    // チーム2（プレイヤー3）が手札0枚なら勝利
    const team2Players = players.filter(p => p.team === Team.TEAM_2);
    if (team2Players.some(p => p.hand.length === 0)) {
      return Team.TEAM_2;
    }

    return null;
  }

  /**
   * 色の宣言が必要か判定
   * @param card プレイされたカード
   * @returns 色宣言が必要ならtrue
   */
  static needsColorDeclaration(card: Card): boolean {
    return card.type === CardType.WILD || card.type === CardType.WILD_DRAW_FOUR;
  }

  /**
   * チームメイトか判定
   * @param player1Id プレイヤー1のID
   * @param player2Id プレイヤー2のID
   * @param players 全プレイヤー配列
   * @returns チームメイトならtrue
   */
  static areTeammates(player1Id: string, player2Id: string, players: Player[]): boolean {
    const p1 = players.find(p => p.id === player1Id);
    const p2 = players.find(p => p.id === player2Id);

    if (!p1 || !p2) return false;
    return p1.team === p2.team;
  }

  /**
   * 手札からダメージを計算
   * @param hand 手札のカード配列
   * @returns 合計ダメージ値
   */
  static calculateDamage(hand: Card[]): number {
    const { DAMAGE_VALUES } = LIFE_POINTS_CONFIG;

    return hand.reduce((total, card) => {
      switch (card.type) {
        case CardType.NUMBER:
          return total + DAMAGE_VALUES.NUMBER(card.value ?? 0);
        case CardType.SKIP:
          return total + DAMAGE_VALUES.SKIP;
        case CardType.REVERSE:
          return total + DAMAGE_VALUES.REVERSE;
        case CardType.DRAW_TWO:
          return total + DAMAGE_VALUES.DRAW_TWO;
        case CardType.WILD:
          return total + DAMAGE_VALUES.WILD;
        case CardType.WILD_DRAW_FOUR:
          return total + DAMAGE_VALUES.WILD_DRAW_FOUR;
        default:
          return total;
      }
    }, 0);
  }

  /**
   * カード1枚のダメージ値を取得
   * @param card カード
   * @returns ダメージ値
   */
  static getCardDamageValue(card: Card): number {
    const { DAMAGE_VALUES } = LIFE_POINTS_CONFIG;

    switch (card.type) {
      case CardType.NUMBER:
        return DAMAGE_VALUES.NUMBER(card.value ?? 0);
      case CardType.SKIP:
        return DAMAGE_VALUES.SKIP;
      case CardType.REVERSE:
        return DAMAGE_VALUES.REVERSE;
      case CardType.DRAW_TWO:
        return DAMAGE_VALUES.DRAW_TWO;
      case CardType.WILD:
        return DAMAGE_VALUES.WILD;
      case CardType.WILD_DRAW_FOUR:
        return DAMAGE_VALUES.WILD_DRAW_FOUR;
      default:
        return 0;
    }
  }
}
